import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { type IntlService } from 'ember-intl';
import { getLogger } from 'log4javascript';
import LogonParams, { type ILogonParams } from 'login/models/logon-params';
import Version from 'login/models/version';
import FlashMessageObject from 'w-flash-messages/models/flash-message-object';
import FlashMessagesService from 'w-flash-messages/services/flash-messages-service';

const log = getLogger('login');

export type ApplicationModel = {
  logonParams: LogonParams;
  version: Version;
};

export default class ApplicationRoute extends Route<ApplicationModel> {
  @service flashMessagesService!: FlashMessagesService;
  @service intl!: IntlService;

  @tracked private applicationModel = {
    logonParams: new LogonParams({
      serviceName: '',
      wallpaperPortrait: '',
      wallpaperLandscape: '',
      useWallpapers: false,
      allowPasskeys: false,
      online: false,
    }),
    version: new Version(),
  };

  private offlineMessage?: FlashMessageObject;

  private async loadTranslations(): Promise<void> {
    const lang = navigator.language;
    const url = `translations/${lang}.json`;
    try {
      const response = await fetch(url);
      const trans = (await response.json()) as Record<string, string>;
      if (log.isTraceEnabled()) {
        log.trace(JSON.stringify(trans));
      }
      this.intl.addTranslations(lang, trans);
      this.intl.setLocale(lang);
    } catch (error) {
      log.error(`Failed to load translations for ${lang}: ${String(error)}`);
    }
  }

  private delayedUpdatingLogonParams = task(async () => {
    await timeout(5000);
    void this.updateLogonParams().finally(() => this.pollLogonParams());
  });

  private pollLogonParams(): void {
    const logonParams = this.applicationModel.logonParams;
    if (!logonParams.online) {
      if (this.offlineMessage === undefined) {
        this.offlineMessage = this.flashMessagesService.add({
          message: this.intl.t('login.prepressServerOffline'),
          type: 'error',
          sticky: true,
        });
      }
      void this.delayedUpdatingLogonParams.perform();
    } else {
      if (this.offlineMessage !== undefined) {
        this.offlineMessage.close();
        this.offlineMessage = undefined;

        this.flashMessagesService.add({
          message: this.intl.t('login.prepressServerOnline'),
          type: 'warning',
          duration: 6000,
        });
      }
    }
  }

  private async updateLogonParams(): Promise<void> {
    let logonParams: ILogonParams;
    try {
      const response = await fetch(`logonParams?_${Date.now()}`);
      logonParams = await (response.json() as Promise<ILogonParams>);
    } catch (error) {
      logonParams = {
        serviceName: '',
        wallpaperPortrait: '',
        wallpaperLandscape: '',
        useWallpapers: false,
        allowPasskeys: false,
        online: false,
      };
      log.error(`Failed to load logonparams: ${String(error)}`);
    }
    if (log.isDebugEnabled()) {
      log.debug(JSON.stringify(logonParams));
    }
    this.applicationModel.logonParams.update(logonParams);
  }

  override async model(): Promise<ApplicationModel> {
    // (window as any).zgfz = getOwner(this);

    await this.loadTranslations();
    await this.updateLogonParams();
    this.pollLogonParams();
    return this.applicationModel;
  }
}
